import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, X, BookOpen, Edit, Eye, Save, UserCheck, UserX, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import Toast from '../../components/Toast';

export default function ManageLecturers() {
  const [lecturers, setLecturers] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedLecturer, setSelectedLecturer] = useState(null);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({ full_name: '', email: '', phone_no: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');

  useEffect(() => { loadData(); }, []);

  const showToastMessage = (message, type = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  const loadData = async () => {
    try {
      const [lecturersRes, gradesRes] = await Promise.all([
        api.get('/admin/lecturers'),
        api.get('/admin/grades')
      ]);
      if (lecturersRes.data.success) setLecturers(lecturersRes.data.lecturers || []);
      if (gradesRes.data.success) setGrades(gradesRes.data.grades || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ full_name: '', email: '', phone_no: '', password: '' });
    setEditMode(false);
    setSelectedLecturer(null);
    setError('');
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (lecturer) => {
    setFormData({
      full_name: lecturer.full_name || '',
      email: lecturer.email || '',
      phone_no: lecturer.phone_no || '',
      password: ''
    });
    setSelectedLecturer(lecturer);
    setEditMode(true);
    setShowModal(true);
  };

  const openViewModal = (lecturer) => {
    setSelectedLecturer(lecturer);
    setShowViewModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.full_name.trim()) {
      showToastMessage('Please enter lecturer name', 'warning');
      return;
    }

    if (!formData.email.trim()) {
      showToastMessage('Please enter email address', 'warning');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToastMessage('Please enter a valid email address', 'warning');
      return;
    }

    // Validate phone number - must be exactly 10 digits
    if (formData.phone_no && !/^\d{10}$/.test(formData.phone_no.replace(/\D/g, ''))) {
      showToastMessage('Phone number must be exactly 10 digits', 'warning');
      return;
    }

    if (!editMode && !formData.password.trim()) {
      showToastMessage('Please enter password for new lecturer', 'warning');
      return;
    }

    if (!editMode && formData.password.length < 8) {
      showToastMessage('Password must be at least 8 characters', 'warning');
      return;
    }

    try {
      if (editMode && selectedLecturer) {
        const res = await api.put(`/admin/lecturers/${selectedLecturer.user_id}`, formData);
        if (res.data.success) {
          showToastMessage('Lecturer updated successfully!', 'success');
          setShowModal(false);
          resetForm();
          loadData();
        }
      } else {
        const res = await api.post('/admin/lecturers', formData);
        if (res.data.success) {
          showToastMessage(`Lecturer created! Login: ${formData.email}`, 'success');
          setShowModal(false);
          resetForm();
          loadData();
        }
      }
    } catch (err) {
      showToastMessage(err.response?.data?.message || 'Failed to save lecturer', 'error');
      setError(err.response?.data?.message || 'Failed to save lecturer');
    }
  };

  const toggleStatus = async (lecturer) => {
    try {
      // Normalize numeric status and toggle explicitly
      const current = Number(lecturer.is_active) === 1 ? 1 : 0;
      const newStatus = current === 1 ? 0 : 1;
      await api.put(`/admin/lecturers/${lecturer.user_id}/status`, { is_active: newStatus });
      showToastMessage(`Lecturer ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`, 'success');
      loadData();
    } catch (err) {
      showToastMessage(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const openAssignModal = (lecturer) => {
    setSelectedLecturer(lecturer);
    // Parse existing grade IDs
    const existingIds = lecturer.grade_ids ? lecturer.grade_ids.split(',').map(id => parseInt(id)).filter(Boolean) : [];
    setSelectedGrades(existingIds);
    setShowAssignModal(true);
  };

  const handleAssignGrades = async () => {
    try {
      if (selectedGrades.length === 0) {
        showToastMessage('Please select at least one grade', 'warning');
        return;
      }
      await api.put(`/admin/lecturers/${selectedLecturer.user_id}/grades`, { grade_ids: selectedGrades });
      showToastMessage('Grades assigned successfully', 'success');
      setShowAssignModal(false);
      loadData();
    } catch (err) {
      showToastMessage(err.response?.data?.message || 'Failed to assign grades', 'error');
    }
  };

  const toggleGrade = (gradeId) => {
    setSelectedGrades(prev =>
      prev.includes(gradeId) ? prev.filter(id => id !== gradeId) : [...prev, gradeId]
    );
  };

  const filteredLecturers = lecturers.filter(l =>
    l.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Manage Lecturers</h1>
          <p className="text-gray-500">Add lecturers and assign subjects</p>
        </div>
        <button onClick={openAddModal} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition">
          <Plus className="w-5 h-5" /> Add Lecturer
        </button>
      </div>

      {/* Toast Notification */}
      <Toast 
        message={toastMessage} 
        type={toastType} 
        visible={showToast} 
        onClose={() => setShowToast(false)}
      />

      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Lecturers ({filteredLecturers.length})</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-50 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 w-64" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lecturer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Assigned Grades</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredLecturers.map((lecturer) => (
                <tr 
                  key={lecturer.user_id} 
                  onClick={() => navigate(`/admin/lecturers/${lecturer.user_id}`)}
                  className="hover:bg-indigo-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <span className="text-purple-600 font-medium">{lecturer.full_name?.charAt(0)}</span>
                      </div>
                      <span className="font-medium text-gray-800">{lecturer.full_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{lecturer.email}</td>
                  <td className="px-4 py-3 text-gray-600">{lecturer.phone_no || '-'}</td>
                  <td className="px-4 py-3">
                    {/* Show grade badges when available, otherwise show subject count with tooltip */}
                    {lecturer.grades ? (
                      <div className="flex flex-wrap gap-2">
                        {lecturer.grades.split(', ').map((g, i) => (
                          <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs">{g}</span>
                        ))}
                      </div>
                    ) : lecturer.subjects ? (
                      <div title={lecturer.subjects} className="text-sm text-gray-600">{(lecturer.subject_count || 0) > 0 ? `${lecturer.subject_count} subject${lecturer.subject_count > 1 ? 's' : ''}` : 'Has subjects'}</div>
                    ) : (
                      <span className="text-gray-400 text-sm">No subjects</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${Number(lecturer.is_active) === 1 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                      {Number(lecturer.is_active) === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => openViewModal(lecturer)} className="p-2 hover:bg-gray-100 text-gray-500 rounded-lg transition" title="View Details">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button onClick={() => openEditModal(lecturer)} className="p-2 hover:bg-blue-50 text-blue-500 rounded-lg transition" title="Edit">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => openAssignModal(lecturer)} className="p-2 hover:bg-indigo-50 text-indigo-600 rounded-lg transition" title="Assign Subjects">
                          <BookOpen className="w-4 h-4" />
                        </button>
                        <button onClick={() => toggleStatus(lecturer)} className={`p-2 rounded-lg transition ${Number(lecturer.is_active) === 1 ? 'hover:bg-red-50 text-red-500' : 'hover:bg-green-50 text-green-500'}`}
                          title={Number(lecturer.is_active) === 1 ? 'Deactivate' : 'Activate'}>
                          {Number(lecturer.is_active) === 1 ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </td>
                </tr>
              ))}
              {filteredLecturers.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-400">No lecturers found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Lecturer Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowModal(false); resetForm(); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">{editMode ? 'Edit Lecturer' : 'Add New Lecturer'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                <input type="text" required value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${!formData.full_name.trim() && error ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${!formData.email.trim() && error ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input type="tel" value={formData.phone_no} onChange={(e) => setFormData({...formData, phone_no: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password {editMode ? '(leave blank to keep current)' : ' *'}
                </label>
                <input type="text" required={!editMode} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder={editMode ? 'Enter new password to change' : 'Enter password'}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition ${!formData.password.trim() && !editMode && error ? 'border-red-500 focus:ring-red-500 focus:ring-2' : 'border-gray-300 focus:ring-indigo-500'}`} />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2">
                  {editMode ? <><Save className="w-4 h-4" /> Update</> : <><Plus className="w-4 h-4" /> Create</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Lecturer Modal */}
      {showViewModal && selectedLecturer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowViewModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Lecturer Details</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex flex-col items-center mb-6">
              <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mb-3">
                <span className="text-purple-600 font-bold text-2xl">{selectedLecturer.full_name?.charAt(0)}</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-800">{selectedLecturer.full_name}</h3>
              <span className={`mt-2 px-3 py-1 rounded-full text-xs font-medium ${Number(selectedLecturer.is_active) === 1 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                {Number(selectedLecturer.is_active) === 1 ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">Email</span>
                <span className="font-medium text-gray-800">{selectedLecturer.email}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">Phone Number</span>
                <span className="font-medium text-gray-800">{(selectedLecturer.phone_number ?? selectedLecturer.phone_no) || '-'}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-gray-100">
                <span className="text-gray-500">User ID</span>
                <span className="font-medium text-gray-800">#{selectedLecturer.user_id}</span>
              </div>
              <div className="py-3">
                <span className="text-gray-500 block mb-2">Assigned Subjects</span>
                <div className="flex flex-wrap gap-2">
                  {selectedLecturer.subjects ? selectedLecturer.subjects.split(', ').map((s, i) => (
                    <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs">{s}</span>
                  )) : <span className="text-gray-400 text-sm">No subjects assigned</span>}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowViewModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Close</button>
              <button onClick={() => { setShowViewModal(false); openEditModal(selectedLecturer); }} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2">
                <Edit className="w-4 h-4" /> Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Grades Modal */}
      {showAssignModal && selectedLecturer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAssignModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Assign Grades to {selectedLecturer.full_name}</h2>
              <button onClick={() => setShowAssignModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>

            <p className="text-sm text-gray-500 mb-4">Select grades to assign. All subjects in the selected grades will be assigned to this lecturer.</p>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {grades.map(grade => (
                <label key={grade.grade_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={selectedGrades.includes(grade.grade_id)}
                    onChange={() => toggleGrade(grade.grade_id)} className="w-4 h-4 text-indigo-600 rounded" />
                  <div>
                    <p className="font-medium text-gray-800">{grade.grade_name}</p>
                    <p className="text-sm text-gray-500">{grade.description || 'No description'}</p>
                  </div>
                </label>
              ))}
              {grades.length === 0 && <p className="text-center text-gray-400 py-4">No grades available</p>}
            </div>

            <div className="flex gap-3 pt-4 mt-4 border-t">
              <button onClick={() => setShowAssignModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAssignGrades} className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save Assignments</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
